// Render the report to a multi-page A4 PDF. Same html2canvas + jsPDF approach as
// components/viewer/ScheduleViewer.jsx, with two rules:
//   1. each employee section (`data-report-employee`) starts on a NEW page, and
//   2. a section taller than one page is sliced across pages.
// Each section is captured as its own canvas — so the per-PDF font enlargement
// (onclone) can change layout height without throwing off any cross-element
// offset maths. The report header (title/period) sits atop the first page.

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const MARGIN_MM = 8;

// PDF-only styling, applied to html2canvas's off-screen copy via `onclone` so
// the on-screen report is untouched. Two things make the text easily readable:
//   1. constrain the capture to ~A4 content width (700px) so it maps roughly 1:1
//      onto the page instead of being shrunk down from the full tab width, and
//   2. bump the font sizes on top of that.
const PDF_WIDTH_PX = 700;
const styleForPdf = (doc) => {
  const style = doc.createElement('style');
  style.textContent = `
    [data-report-root],
    [data-report-root] [data-report-header],
    [data-report-root] [data-report-employee] {
      width: ${PDF_WIDTH_PX}px !important;
      max-width: ${PDF_WIDTH_PX}px !important;
      font-size: 18px !important;
    }
    [data-report-root] .ant-card-head-title { font-size: 22px !important; }
    [data-report-root] .ant-table { font-size: 17px !important; }
    [data-report-root] .ant-table-thead > tr > th { font-size: 17px !important; }
    [data-report-root] .ant-tag { font-size: 16px !important; }
  `;
  doc.head.appendChild(style);
};

const capture = (node) =>
  html2canvas(node, {
    scale: 2, // sharper text than the default 1x
    useCORS: true,
    backgroundColor: '#ffffff',
    width: PDF_WIDTH_PX, // render at the constrained width set above
    windowWidth: PDF_WIDTH_PX,
    onclone: styleForPdf,
  });

/**
 * @param {HTMLElement} el - the report body (a `[data-report-root]` element
 *   containing a `[data-report-header]` block and `[data-report-employee]` cards).
 * @param {string} filename - e.g. `report_2026-06-01_2026-06-30.pdf`.
 */
export const exportElementToPdf = async (el, filename) => {
  if (!el) return;

  const pdf = new jsPDF('portrait', 'mm', 'a4');
  const pageWidthMm = pdf.internal.pageSize.getWidth();
  const pageHeightMm = pdf.internal.pageSize.getHeight();
  const contentWidthMm = pageWidthMm - 2 * MARGIN_MM;

  let cursorMm = MARGIN_MM; // current y on the current page
  let started = false; // has anything been drawn yet?

  const newPage = () => {
    if (started) pdf.addPage();
    cursorMm = MARGIN_MM;
  };

  // Draw one section's canvas from the cursor down, slicing onto fresh pages
  // when it runs past the bottom margin.
  const place = (canvas) => {
    const pxPerMm = canvas.width / contentWidthMm;
    let sourceY = 0;
    while (sourceY < canvas.height) {
      let availableMm = pageHeightMm - MARGIN_MM - cursorMm;
      if (availableMm < 10) {
        pdf.addPage();
        cursorMm = MARGIN_MM;
        availableMm = pageHeightMm - 2 * MARGIN_MM;
      }
      const sliceHeightPx = Math.min(availableMm * pxPerMm, canvas.height - sourceY);

      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceHeightPx;
      pageCanvas
        .getContext('2d')
        .drawImage(canvas, 0, sourceY, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);

      const sliceHeightMm = sliceHeightPx / pxPerMm;
      pdf.addImage(
        pageCanvas.toDataURL('image/png'),
        'PNG',
        MARGIN_MM,
        cursorMm,
        contentWidthMm,
        sliceHeightMm
      );

      cursorMm += sliceHeightMm;
      sourceY += sliceHeightPx;
      started = true;
    }
  };

  // Header on page 1.
  const header = el.querySelector('[data-report-header]');
  if (header) place(await capture(header));

  // Each employee starts on its own page (the first shares page 1 with the header).
  const employees = Array.from(el.querySelectorAll('[data-report-employee]'));
  for (let i = 0; i < employees.length; i += 1) {
    if (i > 0) newPage();
    // eslint-disable-next-line no-await-in-loop
    place(await capture(employees[i]));
  }

  // Fallback: nothing marked up (e.g. empty report) — capture the whole node.
  if (!started) place(await capture(el));

  pdf.save(filename);
};
